import PropTypes from "prop-types";
import React from "react";

import useOutputReportStore from "../../../../store/OutputReport.store";
import { getOutputType } from "../../../../utils/helpers/dataTable";
import BarChart from "../../../Chart/Bar";

import {
  checkZeroData,
  createChartLegend,
  createTransitionSummaryBarTopLabel,
  getChartOptions,
  getColorSequence,
  getDatasetsMinMaxValueZeroCenter,
  getStepSizeAndLabelsMinMax,
  getTransitionSummaryDataset,
} from "../../../../utils/helpers/chart";

const TransitionSummaryChart = ({ id, isFullWidth }) => {
  const reportName = "Transition Summary";
  const data = useOutputReportStore((state) => state.data);
  const { reportType, assetType } = getOutputType(data);

  const isEquityReport = assetType === "equity";
  const options = {
    isLegend: true,
    isFullWidth,
    yTitle: "Tax Savings/Tax Cost",
    yBeginAtZero: true,
    yTicksType: "currency",
    isYannotationReversed: true,
    isXlabelsDisplayed: isFullWidth, // By default the x labels is displayed based on isfullWidth
    isStacked: true,
    yGridEnabled: false,
  };

  const transitionSummaryChartLabels = {
    equity: [
      ["Full Liquidation of Pre-", "Existing Portfolio*"],
      ["Minimize Tax", "Liability**"],
      ["Balance Tax Liability", "& Tracking Error**"],
      ["Minimize Tracking", "Error**"],
    ],
    default: [
      ["Full Liquidation of Pre-", "Existing Portfolio"],
      ["Minimize Tax", "Liability"],
      ["Balance Tax Liability", "& Tracking Error"],
      ["Minimize Tracking", "Error"],
    ],
  };

  const chartId = `${id}-${reportType}`;

  // Stacked Bar Styles
  const chartStyles = getColorSequence();

  // Stacked Bar Data - Y Axis Data
  const chartDatasets = getTransitionSummaryDataset(
    reportName,
    data,
    isEquityReport
  );

  const isZeroData = checkZeroData(chartDatasets);
  if (isZeroData) {
    return null;
  }

  // get dataset max
  const { min, max } = getDatasetsMinMaxValueZeroCenter(chartDatasets);
  //get step size and step labels
  const { stepSize, stepSizeLabels } = getStepSizeAndLabelsMinMax(min, max);

  options.yTicksMin = stepSize > 0 ? min : -8000;
  options.yTicksMax = stepSize > 0 ? max : 8000;
  options.yStepSize = stepSize > 0 ? stepSize : 8000;
  options.yStepSizeLabels = stepSize > 0 ? stepSizeLabels : [-8000, 0, 8000];

  const chartOptions = getChartOptions(options);

  // Stacked Bar X Labels - X Axis Data
  const chartLabels = isEquityReport
    ? transitionSummaryChartLabels.equity
    : transitionSummaryChartLabels.default;

  // Format Chart Data
  const chartData = {
    labels: chartLabels,
    datasets: chartDatasets.map((item, idx) => {
      return {
        ...item,
        ...chartStyles[idx],
      };
    }),
  };

  // Legend for Transition Summary
  const legendOptions = {
    containerId: chartId,
    legendId: `legend-${chartId}`,
    legendTitle: "",
    isReversed: false,
    isFullWidth,
  };
  const chartLegend = createChartLegend(legendOptions);

  const transitionSummaryBarTopLabel = createTransitionSummaryBarTopLabel(
    "transitionSummaryBarTopLabel",
    chartId,
    isFullWidth
  );

  return (
    <BarChart
      id={chartId}
      data={chartData}
      options={chartOptions}
      plugins={[chartLegend, transitionSummaryBarTopLabel]}
      ariaLabel="Transition Summary Report Bar Chart"
      height={isFullWidth ? "420px" : "372px"}
    />
  );
};

TransitionSummaryChart.propTypes = {
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

TransitionSummaryChart.defaultProps = {
  id: "bar-chart-transition-summary",
  isFullWidth: true,
};

export default TransitionSummaryChart;
